// ---------------------------------------------------------------------------
// Test Mode — simulated project meeting notes (CLE-255).
//
// Every project gets >= 3 meeting notes with realistic Romanian transcripts of
// client discussions, an AI-style summary, extracted action items, decisions,
// key facts and open questions. These are served by the mock router on
// GET /api/meeting-notes?projectId=<id> so the project detail "Notițe ședință"
// tab renders fully populated (CLE-255 acceptance criteria).
//
// The 6 CLE-255 projects (pr10..pr15) carry hand-written, project-specific
// notes; the original projects (pr1..pr9) get coherent generated notes so the
// tab is never empty anywhere.
// ---------------------------------------------------------------------------

import type { MeetingNote, MeetingType } from '../lib/meetingNotesApi';
import { isoDate, isoDateTime } from './company';
import { PROJECT_SEEDS, projectName } from './projects';
import { clientName } from './clients';

// A lighter authoring shape; the full MeetingNote is expanded by `toNote`.
interface NoteSeed {
  key: string;
  title: string;
  dayOffset: number; // days from today (negative = past)
  attendees: string;
  meetingType: MeetingType;
  content: string;
  summary: string;
  decisions: string;
  actionItems: string;
  keyFacts: string;
  openQuestions: string;
}

function toNote(projectId: string, seed: NoteSeed): MeetingNote {
  return {
    id: `mn-${projectId}-${seed.key}`,
    clientId: null,
    projectId,
    leadId: null,
    personId: null,
    meetingId: null,
    title: seed.title,
    date: isoDate(seed.dayOffset),
    attendees: seed.attendees,
    meetingType: seed.meetingType,
    content: seed.content,
    summary: seed.summary,
    decisions: seed.decisions,
    actionItems: seed.actionItems,
    keyFacts: seed.keyFacts,
    openQuestions: seed.openQuestions,
    source: 'transcribed',
    s3Key: null,
    s3Url: null,
    audioKey: null,
    ingestStatus: 'COMPLETED',
    ingestLog: null,
    lastIngestedAt: isoDateTime(seed.dayOffset, 16),
    isIndexed: true,
    createdById: null,
    createdAt: isoDateTime(seed.dayOffset, 15),
    updatedAt: isoDateTime(seed.dayOffset, 16),
  };
}

// --- Hand-written notes for the 6 CLE-255 projects -------------------------

const HANDWRITTEN: Record<string, NoteSeed[]> = {
  // RetuRO — platformă SGR (Sistem de Garanție-Returnare) / reciclare
  pr10: [
    {
      key: 'kickoff',
      title: 'Kickoff — definire scope platformă RetuRO SGR',
      dayOffset: -21,
      attendees: 'Andrei Pata (CCC), Mihai Georgescu (CCC), Maria Ionescu (RetuRO SGR)',
      meetingType: 'KICKOFF',
      content:
        'Maria a prezentat viziunea RetuRO: platforma digitală pentru Sistemul de Garanție-Returnare (SGR) — tracking ambalaje cu cod SGR, rețeaua de puncte de colectare (RVM-uri și colectare manuală în retail), raportare către administrator și integrare cu retailerii. Am discutat fluxul principal — înregistrare ambalaj la producător/importator, scanare la returnare, decontare garanție și raportare cantități. Mihai a ridicat problema volumului uriaș de scanări și a propus o arhitectură cu un serviciu de ingestie de evenimente scalabil.\n\nMaria insistă pe trasabilitate completă și raportare conformă cu cerințele legale SGR. Am convenit ca primul livrabil să fie modelul de date pentru ambalaje/puncte de colectare și o propunere de arhitectură pentru ingestia scanărilor.\n\nBugetul și termenul au fost confirmate la nivel de principiu; detaliile contractuale se finalizează săptămâna viitoare.',
      summary:
        'Kickoff RetuRO SGR: platformă de tracking ambalaje, puncte de colectare, raportare și integrare retaileri. Primii pași — model de date și arhitectură ingestie scanări.',
      decisions:
        '- Serviciu de ingestie de evenimente scalabil pentru scanări\n- Trasabilitate completă ambalaj → punct de colectare → raportare',
      actionItems:
        '- [ ] Model de date ambalaje/puncte de colectare (Diana)\n- [ ] Propunere arhitectură ingestie scanări (Mihai)\n- [ ] Wireframes flux returnare și decontare garanție (Cristina)',
      keyFacts:
        '- Volum estimat de milioane de scanări/lună\n- Raportare conformă cu cerințele legale SGR',
      openQuestions:
        '- Ce tipuri de RVM-uri trebuie integrate la lansare?\n- Cum se face decontarea garanției către retaileri?',
    },
    {
      key: 'wireframes',
      title: 'Review wireframes returnare & model de date SGR',
      dayOffset: -10,
      attendees: 'Cristina Stan (CCC), Diana Vasile (CCC), Maria Ionescu (RetuRO SGR)',
      meetingType: 'REVIEW',
      content:
        'Cristina a prezentat wireframes-urile pentru fluxul de returnare și panoul punctului de colectare. Maria a apreciat fluxul, dar a cerut un ecran dedicat de reconciliere zilnică pentru retaileri și un raport de cantități pe tip de material (PET, aluminiu, sticlă). Diana a prezentat modelul de date pentru ambalaje și a confirmat integrarea cu două rețele de RVM-uri prin API, iar pentru colectarea manuală un flux de scanare în aplicație.\n\nAm discutat strategia pentru punctele fără RVM — scanare manuală cu cod SGR și validare la nivel de lot. Maria a acceptat abordarea pentru lansare. Pasul următor: prototip clickabil al fluxului de returnare și reconciliere.',
      summary:
        'Wireframes returnare validate cu ajustări (reconciliere zilnică, raport pe tip material). 2 rețele RVM prin API, colectare manuală prin app.',
      decisions:
        '- Puncte fără RVM: scanare manuală cu cod SGR + validare pe lot\n- Se adaugă ecran de reconciliere zilnică și raport pe tip de material',
      actionItems:
        '- [ ] Prototip clickabil flux returnare + reconciliere (Cristina)\n- [ ] Integrare API rețele RVM #1 și #2 (Diana)\n- [ ] Specificație raport cantități pe tip material (Mihai)',
      keyFacts:
        '- 2 rețele RVM cu API, colectare manuală prin app\n- Reconciliere zilnică cerută de retaileri',
      openQuestions: '- Cum gestionăm discrepanțele între scanări și cantitățile fizice predate?',
    },
    {
      key: 'status',
      title: 'Status sprint 2 — raportare & integrare retaileri',
      dayOffset: -3,
      attendees: 'Mihai Georgescu (CCC), Alexandru Ionescu (CCC), Maria Ionescu (RetuRO SGR)',
      meetingType: 'STATUS',
      content:
        'Alexandru a raportat că fluxul de returnare este funcțional pe staging, cu cele două rețele RVM integrate. Modulul de raportare către administrator este în curs — generăm rapoarte de cantități pe material și pe punct de colectare. Maria a întrebat de decontarea garanției către retaileri; am convenit pe un flux de reconciliere automată cu confirmare manuală în faza 2.\n\nRiscul principal rămâne stabilitatea API-ului unei rețele RVM, care a avut două căderi săptămâna trecută. Mihai propune o coadă de retry cu reluare idempotentă a scanărilor pierdute. Maria a aprobat.',
      summary:
        'Returnare funcțională pe staging cu 2 rețele RVM. Raportare cantități în integrare. Coadă de retry pentru scanările pierdute.',
      decisions:
        '- Reconciliere automată cu confirmare manuală pentru decontarea garanției\n- Coadă de retry idempotentă pentru rețeaua RVM instabilă',
      actionItems:
        '- [ ] Finalizare modul raportare cantități (Alexandru)\n- [ ] Implementare coadă de retry scanări (Diana)\n- [ ] Flux reconciliere decontare garanție — planificat faza 2 (Mihai)',
      keyFacts:
        '- O rețea RVM a avut 2 căderi în ultima săptămână\n- Raportarea conformă SGR e cerință obligatorie',
      openQuestions: '- Termenul exact pentru fluxul de decontare automată din faza 2?',
    },
  ],
  // FUNCURIER — curierat / logistică last-mile
  pr11: [
    {
      key: 'kickoff',
      title: 'Kickoff — platformă last-mile FunCurier',
      dayOffset: -45,
      attendees: 'Andrei Pata (CCC), Elena Dumitrescu (CCC), Robert Dumitru (FunCurier)',
      meetingType: 'KICKOFF',
      content:
        'Robert a explicat că FunCurier vrea să înlocuiască un sistem manual de dispecerizare cu o platformă care optimizează rutele și oferă tracking în timp real clienților. Volumul actual e de ~1.500 colete/zi în 3 orașe, cu plan de extindere. Elena a propus o arhitectură cu un serviciu de optimizare rute separat și un canal de evenimente pentru actualizările de status.\n\nAm discutat aplicația de curier (mobil) — scanare colet, confirmare livrare cu semnătură/foto, navigație. Robert a subliniat că șoferii au telefoane modeste, deci aplicația trebuie să fie ușoară. Stabilim ca primul livrabil să fie modelul de date pentru comenzi/rute și un PoC de optimizare rute.',
      summary:
        'Kickoff FunCurier: platformă last-mile cu optimizare rute, tracking live și app de curier ușoară. ~1.500 colete/zi în 3 orașe.',
      decisions:
        '- Serviciu de optimizare rute separat\n- Canal de evenimente pentru actualizări de status\n- App de curier optimizată pentru telefoane modeste',
      actionItems:
        '- [ ] Model de date comenzi/rute (Bogdan)\n- [ ] PoC optimizare rute (Vlad)\n- [ ] Wireframes app curier (Elena)',
      keyFacts:
        '- ~1.500 colete/zi, 3 orașe, în creștere\n- Șoferii folosesc telefoane cu resurse limitate',
      openQuestions:
        '- Integrăm cu un serviciu de hărți comercial sau open-source?\n- Cerințe legale pentru semnătura electronică de livrare?',
    },
    {
      key: 'poc',
      title: 'Review PoC optimizare rute',
      dayOffset: -25,
      attendees: 'Vlad Popescu (CCC), Bogdan Marin (CCC), Robert Dumitru (FunCurier)',
      meetingType: 'REVIEW',
      content:
        'Vlad a prezentat PoC-ul de optimizare a rutelor pe un set de date real de 1.200 livrări. Algoritmul reduce distanța totală cu ~18% față de alocarea manuală. Robert a fost impresionat, dar a cerut să se țină cont de ferestrele orare cerute de clienți (livrare AM/PM). Bogdan a confirmat că modelul de date suportă deja ferestre orare, dar algoritmul trebuie ajustat.\n\nAm discutat și costul serviciului de hărți; pentru volum, varianta comercială devine scumpă. Robert preferă open-source pentru rutare și hărți comerciale doar pentru afișare. Decizie aprobată.',
      summary:
        'PoC optimizare rute reduce distanța cu ~18%. De adăugat ferestre orare AM/PM. Rutare open-source, hărți comerciale doar pentru afișare.',
      decisions:
        '- Rutare pe stack open-source, hărți comerciale doar la afișare\n- Algoritmul va respecta ferestrele orare ale clienților',
      actionItems:
        '- [ ] Ajustare algoritm pentru ferestre orare (Vlad)\n- [ ] Integrare provider hărți pentru afișare (Bogdan)\n- [ ] Estimare cost lunar hărți la volum țintă (Elena)',
      keyFacts:
        '- Reducere distanță ~18% pe 1.200 livrări reale\n- Hărțile comerciale sunt scumpe la volum',
      openQuestions: '- Ce SLA oferim pentru actualizarea poziției curierului în tracking-ul live?',
    },
    {
      key: 'risk',
      title: 'Status & risc — stabilitate tracking live',
      dayOffset: -6,
      attendees: 'Elena Dumitrescu (CCC), Raluca Florea (CCC), Robert Dumitru (FunCurier)',
      meetingType: 'STATUS',
      content:
        'Raluca a raportat că tracking-ul live funcționează, dar la vârf de trafic (dimineața) latența actualizărilor crește. A propus trecerea de la polling la un canal push (WebSocket) și scalare orizontală a serviciului de evenimente. Robert a confirmat că vârful de dimineață e critic pentru experiența clientului.\n\nElena a semnalat o întârziere la app-ul de curier din cauza testării pe dispozitive low-end. Robert va furniza 3 telefoane reprezentative pentru testare. Proiectul rămâne la risc moderat din cauza acestor două puncte, dar avem plan de remediere clar.',
      summary:
        'Tracking live funcțional dar cu latență la vârf — se trece pe WebSocket + scalare. Întârziere app curier; clientul furnizează dispozitive de test.',
      decisions:
        '- Tracking live pe WebSocket cu scalare orizontală\n- Testare app curier pe dispozitive low-end furnizate de client',
      actionItems:
        '- [ ] Migrare tracking la WebSocket (Raluca)\n- [ ] Scalare serviciu de evenimente (Raluca)\n- [ ] Furnizare 3 telefoane de test (Robert / FunCurier)',
      keyFacts:
        '- Vârful de dimineață e critic pentru tracking\n- App-ul trebuie validat pe hardware modest',
      openQuestions: '- Care e bugetul de infrastructură acceptat pentru scalarea la vârf?',
    },
  ],
  // DSU — sistem guvernamental situații de urgență
  pr12: [
    {
      key: 'requirements',
      title: 'Cerințe sistem alertare și coordonare situații de urgență',
      dayOffset: -60,
      attendees: 'Andrei Pata (CCC), Ioana Pop (CCC), Col. Vasile Gheorghe (DSU)',
      meetingType: 'DISCOVERY',
      content:
        'Col. Gheorghe a prezentat nevoia DSU pentru un sistem unificat de alertare și coordonare a intervențiilor în situații de urgență. Cerințe nefuncționale dure: disponibilitate 99.9% (SLA), integrare cu sistemele existente de comunicații, conformitate GDPR și cerințe stricte de securitate (date sensibile, posibil clasificate la un nivel).\n\nIoana a propus o arhitectură high-availability cu redundanță geografică și un audit log imutabil pentru toate acțiunile operatorilor. Am discutat fluxul de escaladare a alertelor și nevoia de a funcționa degradat când conectivitatea e limitată. Col. Gheorghe a subliniat că orice downtime în timpul unei urgențe e inacceptabil.\n\nAm convenit ca primii livrabili să fie documentul de cerințe de securitate și propunerea de arhitectură HA, înainte de orice cod.',
      summary:
        'Discovery DSU: sistem HA de alertare/coordonare urgențe, SLA 99.9%, GDPR, securitate strictă, funcționare degradată la conectivitate limitată.',
      decisions:
        '- Arhitectură high-availability cu redundanță geografică\n- Audit log imutabil pentru acțiunile operatorilor\n- Securitate și conformitate validate înainte de implementare',
      actionItems:
        '- [ ] Document cerințe de securitate (Ioana)\n- [ ] Propunere arhitectură HA + redundanță (Cătălin)\n- [ ] Timeline pe faze cu poartă de securitate (Mihai)',
      keyFacts:
        '- SLA țintă 99.9%, downtime în urgență inacceptabil\n- Date sensibile, conformitate GDPR obligatorie\n- Necesită mod degradat la conectivitate redusă',
      openQuestions:
        '- Care e nivelul exact de clasificare al datelor?\n- Cine certifică securitatea înainte de go-live?',
    },
    {
      key: 'security',
      title: 'Review arhitectură HA & cerințe de securitate',
      dayOffset: -38,
      attendees:
        'Ioana Pop (CCC), Cătălin Barbu (CCC), Raluca Florea (CCC), Col. Vasile Gheorghe (DSU)',
      meetingType: 'REVIEW',
      content:
        'Cătălin a prezentat arhitectura HA: două centre de date active-active, replicare sincronă pentru datele critice și un mecanism de failover automat sub 30 de secunde. Raluca a detaliat planul de securitate — criptare la rest și în tranzit, MFA obligatoriu pentru operatori, segmentare de rețea și un proces de hardening pe imagini.\n\nCol. Gheorghe a aprobat direcția, dar a cerut ca failover-ul să fie testat lunar cu un exercițiu real. A confirmat că certificarea de securitate se va face de o echipă internă DSU înainte de go-live. Am discutat și retenția log-urilor de audit — minim 5 ani.',
      summary:
        'Arhitectură HA active-active cu failover <30s aprobată. Securitate: criptare, MFA, segmentare, hardening. Audit log păstrat 5 ani; failover testat lunar.',
      decisions:
        '- Active-active cu failover automat sub 30s, testat lunar\n- MFA obligatoriu, criptare la rest și în tranzit\n- Retenție audit log minim 5 ani',
      actionItems:
        '- [ ] Implementare failover automat + runbook test lunar (Cătălin)\n- [ ] Hardening imagini și segmentare rețea (Raluca)\n- [ ] Politică retenție audit log 5 ani (Ioana)',
      keyFacts:
        '- Failover țintă <30s\n- Certificare de securitate făcută intern de DSU\n- Retenție log audit 5 ani',
      openQuestions: '- Exercițiul lunar de failover se face în fereastră programată sau aleator?',
    },
    {
      key: 'pilot',
      title: 'Planificare pilot regional & integrare comunicații',
      dayOffset: -12,
      attendees: 'Mihai Georgescu (CCC), Bogdan Marin (CCC), Col. Vasile Gheorghe (DSU)',
      meetingType: 'PLANNING',
      content:
        'Am planificat un pilot într-o singură regiune înainte de extinderea națională. Bogdan a raportat progres la integrarea cu sistemul de comunicații existent, dar protocolul vechi e slab documentat; au fost necesare sesiuni de reverse engineering cu suportul unui specialist DSU. Col. Gheorghe va aloca acel specialist.\n\nMihai a propus criterii clare de succes pentru pilot: zero pierderi de alerte, failover reușit într-un exercițiu real și feedback pozitiv de la operatori. Col. Gheorghe a fost de acord și a stabilit fereastra pilotului pentru luna următoare.',
      summary:
        'Pilot regional planificat înainte de extindere națională. Integrarea cu comunicațiile vechi necesită un specialist DSU. Criterii pilot: zero alerte pierdute, failover ok, feedback operatori.',
      decisions:
        '- Pilot într-o singură regiune înainte de roll-out național\n- Criterii de succes: zero alerte pierdute + failover reușit + feedback operatori',
      actionItems:
        '- [ ] Alocare specialist comunicații DSU (Col. Gheorghe / DSU)\n- [ ] Finalizare integrare protocol comunicații (Bogdan)\n- [ ] Plan și criterii pilot regional (Mihai)',
      keyFacts:
        '- Protocolul de comunicații vechi e slab documentat\n- Pilot regional luna următoare',
      openQuestions: '- Ce regiune este aleasă pentru pilot și cine sunt operatorii implicați?',
    },
  ],
  // MAE — portal digital
  pr13: [
    {
      key: 'kickoff',
      title: 'Kickoff — portal servicii consulare MAE',
      dayOffset: -55,
      attendees: 'Andrei Pata (CCC), Ioana Pop (CCC), Daniela Stoica (MAE)',
      meetingType: 'KICKOFF',
      content:
        'Daniela a descris obiectivul MAE: un portal unde cetățenii români din străinătate pot solicita servicii consulare online (programări, documente, asistență). Cerințe cheie — autentificare cu eIDAS, accesibilitate WCAG 2.1 AA și suport multilingv. Ioana a propus o arhitectură modulară pe servicii consulare, cu un strat de identitate care abstractizează eIDAS.\n\nAm discutat volumul: zeci de mii de cetățeni, vârfuri sezoniere (vacanțe). Daniela a subliniat că accesibilitatea nu e negociabilă — portalul trebuie să funcționeze pentru persoane cu dizabilități. Stabilim ca primii livrabili să fie fluxul de programare consulară și integrarea eIDAS.',
      summary:
        'Kickoff MAE: portal servicii consulare online, eIDAS, WCAG 2.1 AA, multilingv. Volume mari cu vârfuri sezoniere.',
      decisions:
        '- Arhitectură modulară pe servicii consulare\n- Strat de identitate care abstractizează eIDAS\n- Accesibilitate WCAG 2.1 AA obligatorie',
      actionItems:
        '- [ ] Flux programare consulară (Alexandru)\n- [ ] Integrare autentificare eIDAS (Andreea)\n- [ ] Audit de accesibilitate pe machete (Radu / QA)',
      keyFacts:
        '- Autentificare eIDAS obligatorie\n- WCAG 2.1 AA non-negociabil\n- Vârfuri sezoniere de trafic',
      openQuestions:
        '- Câte limbi suportăm la lansare?\n- Care servicii consulare intră în prima fază?',
    },
    {
      key: 'eidas',
      title: 'Review integrare eIDAS & accesibilitate',
      dayOffset: -30,
      attendees: 'Andreea Toma (CCC), Radu Constantin (CCC), Daniela Stoica (MAE)',
      meetingType: 'REVIEW',
      content:
        'Andreea a prezentat integrarea eIDAS într-un mediu de test; autentificarea funcționează pentru două scheme naționale, dar documentația pentru altele e incompletă. Daniela va facilita contactul cu echipa tehnică eIDAS pentru clarificări. Radu a rulat un audit de accesibilitate pe fluxul de programare și a găsit câteva probleme de contrast și navigare cu tastatura, deja în remediere.\n\nDaniela a cerut ca toate mesajele de eroare să fie clare și traduse, nu coduri tehnice. Am convenit pe un ghid de conținut accesibil pentru toate textele din portal.',
      summary:
        'eIDAS funcțional pentru 2 scheme; restul necesită clarificări. Audit accesibilitate cu probleme minore în remediere. Ghid de conținut accesibil convenit.',
      decisions:
        '- Mesaje de eroare clare și traduse, fără coduri tehnice\n- Ghid de conținut accesibil pentru toate textele',
      actionItems:
        '- [ ] Clarificare scheme eIDAS suplimentare (Daniela / MAE)\n- [ ] Remediere probleme de contrast și tastatură (Andreea)\n- [ ] Redactare ghid de conținut accesibil (Radu)',
      keyFacts:
        '- 2 scheme eIDAS funcționale în test\n- Probleme de accesibilitate minore identificate și în remediere',
      openQuestions: '- Există un termen legal pentru conformitatea completă WCAG?',
    },
    {
      key: 'load',
      title: 'Status — pregătire vârf sezonier & teste de încărcare',
      dayOffset: -8,
      attendees: 'Ioana Pop (CCC), Alexandru Ionescu (CCC), Daniela Stoica (MAE)',
      meetingType: 'STATUS',
      content:
        'Alexandru a raportat că fluxul de programare e complet și că au început testele de încărcare pentru a valida comportamentul la vârfurile sezoniere. Rezultatele inițiale arată că serviciul de programări devine bottleneck la concurență mare; soluția propusă e o coadă pentru cererile de programare și auto-scaling.\n\nDaniela a confirmat datele istorice de trafic pentru a calibra testele. Ioana a subliniat că lansarea trebuie să prindă înainte de sezonul de vacanțe. Proiectul e on-track, cu testele de încărcare drept prioritate imediată.',
      summary:
        'Flux de programare complet; teste de încărcare în curs. Bottleneck la programări — se adaugă coadă + auto-scaling. Țintă: lansare înainte de sezonul de vacanțe.',
      decisions:
        '- Coadă pentru cererile de programare + auto-scaling\n- Calibrare teste pe baza datelor istorice MAE',
      actionItems:
        '- [ ] Implementare coadă programări + auto-scaling (Alexandru)\n- [ ] Furnizare date istorice de trafic (Daniela / MAE)\n- [ ] Rulare teste de încărcare la vârf țintă (Andreea)',
      keyFacts:
        '- Serviciul de programări e bottleneck la concurență mare\n- Lansare planificată înainte de sezonul de vacanțe',
      openQuestions: '- Care e numărul maxim de cereri concurente pe care îl țintim?',
    },
  ],
  // Digital Print — e-commerce print-on-demand
  pr14: [
    {
      key: 'kickoff',
      title: 'Kickoff — platformă print-on-demand',
      dayOffset: -40,
      attendees: 'Elena Dumitrescu (CCC), Vlad Popescu (CCC), Cristian Moldovan (Digital Print)',
      meetingType: 'KICKOFF',
      content:
        'Cristian vrea o platformă e-commerce unde clienții configurează produse personalizate (tricouri, căni, postere) și plasează comenzi care intră direct în fluxul de producție. Elena a propus un configurator de produs cu previzualizare live și generarea automată a unui bon de comandă pentru atelier. Vlad a discutat integrarea plăților și gestiunea stocurilor de materiale.\n\nCristian a subliniat că marja e mică, deci automatizarea fluxului de producție e esențială pentru profitabilitate. Primii livrabili: configuratorul de produs și modelul de comandă cu bon de producție.',
      summary:
        'Kickoff Digital Print: e-commerce print-on-demand cu configurator live, bon de producție automat și plăți. Automatizarea fluxului e cheia profitabilității.',
      decisions:
        '- Configurator de produs cu previzualizare live\n- Generare automată bon de comandă pentru atelier',
      actionItems:
        '- [ ] Configurator de produs cu preview (Vlad)\n- [ ] Model comandă + bon de producție (Cristina)\n- [ ] Integrare plăți și stocuri materiale (Gabriela)',
      keyFacts:
        '- Marjă mică → automatizare obligatorie\n- Produse: tricouri, căni, postere personalizate',
      openQuestions: '- Câte tipuri de produse intră în prima versiune?',
    },
    {
      key: 'configurator',
      title: 'Review configurator & flux producție',
      dayOffset: -20,
      attendees: 'Vlad Popescu (CCC), Cristina Stan (CCC), Cristian Moldovan (Digital Print)',
      meetingType: 'REVIEW',
      content:
        'Vlad a demonstrat configuratorul cu previzualizare live pentru tricouri și căni. Cristian a fost mulțumit de UX, dar a cerut validări de calitate pentru imaginile încărcate (rezoluție minimă) ca să evite tipăriri proaste. Cristina a prezentat formatul bonului de producție, validat de atelier — include poziționare, dimensiuni și culori.\n\nAm discutat fluxul de la comandă la producție: comanda plătită generează automat bonul și intră în coada atelierului. Cristian a confirmat că asta elimină munca manuală actuală. Decizie: validarea rezoluției imaginii devine obligatorie la upload.',
      summary:
        'Configurator live validat (tricouri, căni). Se adaugă validare rezoluție imagine. Bon de producție automat la comandă plătită, validat de atelier.',
      decisions:
        '- Validare obligatorie a rezoluției imaginilor la upload\n- Comanda plătită generează automat bonul de producție',
      actionItems:
        '- [ ] Validare rezoluție imagine la upload (Vlad)\n- [ ] Automatizare generare bon la plată (Gabriela)\n- [ ] Extindere configurator pentru postere (Cristina)',
      keyFacts:
        '- Atelierul a validat formatul bonului de producție\n- Imaginile sub rezoluția minimă cauzează tipăriri proaste',
      openQuestions: '- Cum gestionăm comenzile cu materiale lipsă din stoc?',
    },
    {
      key: 'launch',
      title: 'Pre-lansare — review final & stocuri',
      dayOffset: -4,
      attendees: 'Elena Dumitrescu (CCC), Gabriela Neagu (CCC), Cristian Moldovan (Digital Print)',
      meetingType: 'REVIEW',
      content:
        'Gabriela a raportat că integrarea plăților și gestiunea stocurilor de materiale sunt finalizate; când un material scade sub prag, comenzile care îl folosesc sunt marcate "în așteptare stoc" și atelierul e notificat. Cristian a apreciat și a cerut un mic raport zilnic cu comenzile blocate de stoc.\n\nElena a confirmat că platforma e gata de lansare după ultimul rond de testare. Cristian va furniza catalogul final de produse. Proiectul e aproape finalizat (ALMOST_DONE), cu go-live planificat săptămâna viitoare.',
      summary:
        'Plăți și stocuri finalizate, cu marcaj "în așteptare stoc" și notificare atelier. De adăugat raport zilnic comenzi blocate. Go-live săptămâna viitoare.',
      decisions:
        '- Comenzile fără stoc se marchează "în așteptare stoc" + notificare atelier\n- Go-live după ultimul rond de testare',
      actionItems:
        '- [ ] Raport zilnic comenzi blocate de stoc (Gabriela)\n- [ ] Furnizare catalog final de produse (Cristian / Digital Print)\n- [ ] Ultim rond de testare pre-lansare (Elena)',
      keyFacts:
        '- Notificare automată atelier la stoc scăzut\n- Go-live planificat săptămâna viitoare',
      openQuestions:
        '- Oferim notificare clientului când comanda blocată de stoc revine în producție?',
    },
  ],
  // Let's Go Travel — platformă rezervări
  pr15: [
    {
      key: 'progress',
      title: 'Review progres platformă rezervări',
      dayOffset: -42,
      attendees: "Andrei Pata (CCC), Alexandru Caravela (CCC), Dan Popescu (Let's Go Travel)",
      meetingType: 'REVIEW',
      content:
        'Dan a oferit feedback pe fluxul de booking existent: pașii de rezervare sunt prea mulți și search-ul e lent la multe rezultate. Alexandru Caravela a propus comasarea pașilor de checkout și optimizarea search-ului prin indexare și paginare. Am discutat și integrarea plăților online cu Stripe, cerută pentru a reduce abandonul.\n\nDan a subliniat că abandonul la checkout e principala problemă de business. Am convenit pe trei direcții: optimizare search, redesign checkout și plăți Stripe. Stabilim măsurători înainte/după pentru rata de conversie.',
      summary:
        "Review Let's Go Travel: search lent și checkout prea lung cauzează abandon. Direcții — optimizare search, redesign checkout, plăți Stripe. Se măsoară conversia.",
      decisions:
        '- Comasarea pașilor de checkout\n- Optimizare search prin indexare + paginare\n- Integrare plăți online Stripe',
      actionItems:
        '- [ ] Optimizare search (indexare + paginare) (Diana)\n- [ ] Redesign flux de checkout (Cristina)\n- [ ] Testare integrare plăți Stripe (Sorin)',
      keyFacts:
        '- Abandonul la checkout e principala problemă de business\n- Search lent la multe rezultate',
      openQuestions: '- Care e rata de conversie actuală pe care vrem să o îmbunătățim?',
    },
    {
      key: 'checkout',
      title: 'Review redesign checkout & rezultate search',
      dayOffset: -22,
      attendees: "Cristina Stan (CCC), Diana Vasile (CCC), Dan Popescu (Let's Go Travel)",
      meetingType: 'REVIEW',
      content:
        'Cristina a prezentat noul checkout într-un singur ecran cu rezumat clar al rezervării. Dan a apreciat simplitatea. Diana a raportat că optimizarea search-ului a redus timpul de răspuns de la ~3s la sub 600ms pentru interogări tipice, prin indexare și paginare. Dan a fost foarte mulțumit de îmbunătățirea de performanță.\n\nAm discutat plățile Stripe — fluxul de plată funcționează în test, urmează validarea cu carduri reale și 3D Secure. Stabilim un test A/B pe noul checkout pentru a măsura impactul asupra conversiei.',
      summary:
        'Checkout într-un singur ecran validat. Search optimizat de la ~3s la <600ms. Stripe funcțional în test; urmează 3D Secure și A/B test pe checkout.',
      decisions:
        '- Checkout într-un singur ecran cu rezumat rezervare\n- Test A/B pe noul checkout pentru a măsura conversia',
      actionItems:
        '- [ ] Validare Stripe cu carduri reale + 3D Secure (Sorin)\n- [ ] Configurare test A/B checkout (Diana)\n- [ ] Ajustări finale UI checkout (Cristina)',
      keyFacts: '- Timp search redus de la ~3s la <600ms\n- Checkout redus la un singur ecran',
      openQuestions: '- Pe ce durată rulăm testul A/B înainte de a decide?',
    },
    {
      key: 'status',
      title: 'Status — plăți Stripe & pregătire lansare',
      dayOffset: -5,
      attendees: "Elena Dumitrescu (CCC), Sorin Lazar (CCC), Dan Popescu (Let's Go Travel)",
      meetingType: 'STATUS',
      content:
        'Sorin a raportat că integrarea Stripe e completă, inclusiv 3D Secure și gestionarea rambursărilor. Testul A/B pe noul checkout arată o creștere a conversiei de ~12% față de varianta veche. Dan a fost încântat și a aprobat lansarea noului checkout pentru tot traficul.\n\nElena a semnalat că mai rămâne configurarea webhook-urilor Stripe pentru reconcilierea automată a plăților. Dan a cerut și un raport de încasări pe zi. Proiectul e on-track pentru lansarea completă.',
      summary:
        'Stripe complet (3D Secure, rambursări). A/B test: +~12% conversie pe noul checkout, aprobat pentru tot traficul. Rămân webhook-uri de reconciliere și raport de încasări.',
      decisions:
        '- Lansarea noului checkout pentru tot traficul (+~12% conversie)\n- Reconciliere automată a plăților prin webhook-uri Stripe',
      actionItems:
        '- [ ] Configurare webhook-uri Stripe pentru reconciliere (Sorin)\n- [ ] Raport zilnic de încasări (Diana)\n- [ ] Plan de lansare completă (Elena)',
      keyFacts:
        '- A/B test: +~12% conversie pe noul checkout\n- Stripe acoperă 3D Secure și rambursări',
      openQuestions: '- Vrem și plată prin portofele digitale (Apple Pay / Google Pay) în faza 2?',
    },
  ],
};

// --- Generated notes for the original projects (pr1..pr9) ------------------
//
// Coherent, lighter notes so every project's tab is populated. Three per
// project: a kickoff, a mid-project review and a recent status.

function generatedNotes(projectId: string): NoteSeed[] {
  const seed = PROJECT_SEEDS.find((p) => p.id === projectId);
  const name = projectName(projectId);
  const client = clientName(seed?.clientId ?? '');
  return [
    {
      key: 'kickoff',
      title: `Kickoff — ${name}`,
      dayOffset: (seed?.startOffset ?? -60) + 2,
      attendees: `Andrei Pata (CCC), Mihai Georgescu (CCC), reprezentant ${client}`,
      meetingType: 'KICKOFF',
      content:
        `Întâlnire de început pentru proiectul „${name}” cu ${client}. Am aliniat scope-ul, ` +
        `obiectivele de business și principalele milestone-uri. Echipa a prezentat abordarea ` +
        `tehnică propusă, iar clientul a confirmat prioritățile pentru prima fază. Am stabilit ` +
        `ritmul de comunicare (status săptămânal) și canalul de raportare.`,
      summary: `Kickoff ${name}: scope, obiective și milestone-uri aliniate cu ${client}.`,
      decisions: '- Status săptămânal\n- Prioritizarea fazei 1 conform cerințelor clientului',
      actionItems:
        '- [ ] Pregătire backlog inițial (PM)\n- [ ] Propunere arhitectură (Dev Lead)\n- [ ] Setup mediu de staging (DevOps)',
      keyFacts: `- Client: ${client}\n- Livrare pe faze cu status săptămânal`,
      openQuestions: '- Sunt dependențe externe care pot afecta termenul?',
    },
    {
      key: 'review',
      title: `Review intermediar — ${name}`,
      dayOffset: Math.round(((seed?.startOffset ?? -60) + (seed?.endOffset ?? 30)) / 2),
      attendees: `Mihai Georgescu (CCC), echipa de dezvoltare, reprezentant ${client}`,
      meetingType: 'REVIEW',
      content:
        `Demo intermediar pentru „${name}”. Am parcurs funcționalitățile livrate până acum și ` +
        `am colectat feedback de la ${client}. Câteva ajustări de UX au fost cerute și prioritizate ` +
        `pentru sprintul următor. Indicatorii de progres sunt în linie cu planul.`,
      summary: `Review intermediar ${name}: demo livrat, feedback colectat și prioritizat.`,
      decisions: '- Ajustările de UX intră în sprintul următor',
      actionItems: '- [ ] Implementare ajustări UX (Dev)\n- [ ] Actualizare plan de testare (QA)',
      keyFacts: '- Progres în linie cu planul',
      openQuestions: '- Mai sunt cerințe noi pentru faza finală?',
    },
    {
      key: 'status',
      title: `Status recent — ${name}`,
      dayOffset: -4,
      attendees: `Mihai Georgescu (CCC), reprezentant ${client}`,
      meetingType: 'STATUS',
      content:
        `Status recent pe „${name}”. Echipa a raportat progresul pe sarcinile curente și riscurile ` +
        `deschise. ${client} a confirmat că direcția e bună. Am revizuit planul până la livrarea ` +
        `finală și ne-am asigurat că nu există blocaje critice.`,
      summary: `Status ${name}: progres conform planului, fără blocaje critice.`,
      decisions: '- Se menține planul de livrare curent',
      actionItems: '- [ ] Continuare sarcini în lucru (Dev)\n- [ ] Pregătire UAT (QA + PM)',
      keyFacts: '- Fără blocaje critice raportate',
      openQuestions: '- Confirmăm data de go-live?',
    },
  ];
}

// --- Public API ------------------------------------------------------------

let cached: MeetingNote[] | null = null;

// All project meeting notes across the simulated company.
export function buildMeetingNotes(): MeetingNote[] {
  if (cached) return cached;
  const notes: MeetingNote[] = [];
  for (const project of PROJECT_SEEDS) {
    const seeds = HANDWRITTEN[project.id] ?? generatedNotes(project.id);
    for (const s of seeds) notes.push(toNote(project.id, s));
  }
  cached = notes;
  return notes;
}

// Notes for a single project (drives /api/meeting-notes?projectId=).
export function meetingNotesForProject(projectId: string): MeetingNote[] {
  return buildMeetingNotes().filter((n) => n.projectId === projectId);
}

// A single note by id.
export function buildMeetingNote(id: string): MeetingNote | null {
  return buildMeetingNotes().find((n) => n.id === id) ?? null;
}

// --- Client & Person meeting notes (demo audit) ---------------------------

import { CLIENT_SEEDS } from './clients';
import { PEOPLE_REFS } from './people';

function clientNote(clientId: string, key: string, title: string, dayOffset: number): MeetingNote {
  const client = CLIENT_SEEDS.find((c) => c.id === clientId);
  const name = client?.name ?? 'Client';
  return {
    id: `mn-client-${clientId}-${key}`,
    clientId,
    projectId: null,
    leadId: null,
    personId: null,
    meetingId: null,
    title,
    date: isoDate(dayOffset),
    attendees: `Andrei Pata (CCC), ${client?.contactPerson ?? 'Reprezentant client'}`,
    meetingType: key === 'kickoff' ? 'KICKOFF' : 'STATUS',
    content: `Discuție cu ${name} despre colaborarea curentă și planurile viitoare.`,
    summary: `Întâlnire ${name}: status colaborare și direcții viitoare.`,
    decisions: '- Se continuă colaborarea pe direcțiile actuale',
    actionItems: '- [ ] Follow-up pe subiectele discutate',
    keyFacts: `- Client: ${name}`,
    openQuestions: '- Există oportunități de extindere?',
    source: 'manual',
    s3Key: null,
    s3Url: null,
    audioKey: null,
    ingestStatus: 'COMPLETED',
    ingestLog: null,
    lastIngestedAt: isoDateTime(dayOffset, 16),
    isIndexed: true,
    createdById: null,
    createdAt: isoDateTime(dayOffset, 15),
    updatedAt: isoDateTime(dayOffset, 16),
  };
}

export function meetingNotesForClient(clientId: string): MeetingNote[] {
  const client = CLIENT_SEEDS.find((c) => c.id === clientId);
  if (!client) return [];
  return [
    clientNote(clientId, 'kickoff', `Kickoff colaborare — ${client.name}`, -45),
    clientNote(clientId, 'status', `Status lunar — ${client.name}`, -15),
    clientNote(clientId, 'review', `Review trimestrial — ${client.name}`, -5),
  ];
}

function personNote(personId: string, key: string, title: string, dayOffset: number): MeetingNote {
  const person = PEOPLE_REFS.find((p) => p.id === personId);
  const name = person?.name ?? 'Coleg';
  return {
    id: `mn-person-${personId}-${key}`,
    clientId: null,
    projectId: null,
    leadId: null,
    personId,
    meetingId: null,
    title,
    date: isoDate(dayOffset),
    attendees: `Andrei Pata, ${name}`,
    meetingType: 'STATUS',
    content: `1:1 cu ${name} — status sarcini, feedback și obiective.`,
    summary: `1:1 ${name}: progres bun, obiective aliniate.`,
    decisions: '- Se mențin prioritățile curente',
    actionItems: '- [ ] Follow-up pe sarcinile prioritare',
    keyFacts: `- Persoană: ${name}`,
    openQuestions: '- Sunt blocaje de rezolvat?',
    source: 'manual',
    s3Key: null,
    s3Url: null,
    audioKey: null,
    ingestStatus: 'COMPLETED',
    ingestLog: null,
    lastIngestedAt: isoDateTime(dayOffset, 16),
    isIndexed: true,
    createdById: null,
    createdAt: isoDateTime(dayOffset, 14),
    updatedAt: isoDateTime(dayOffset, 16),
  };
}

export function meetingNotesForPerson(personId: string): MeetingNote[] {
  const person = PEOPLE_REFS.find((p) => p.id === personId);
  if (!person) return [];
  return [
    personNote(personId, '1on1', `1:1 cu ${person.name}`, -14),
    personNote(personId, 'review', `Review performanță — ${person.name}`, -3),
  ];
}
